import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api';

export default function StudentView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [fees, setFees] = useState([]);

  useEffect(() => {
    api.get(`/students/${id}`).then(r => setStudent(r.data));
    api.get('/fees', { params: { student: id } }).then(r => setFees(r.data));
  }, [id]);

  if (!student) return <Layout title="Student Detail"><div style={{ padding: 40, textAlign: 'center' }}>Loading...</div></Layout>;

  const totalPaid = fees.filter(f => f.status === 'Paid').reduce((s, f) => s + f.amount, 0);
  const totalUnpaid = fees.filter(f => f.status === 'Unpaid').reduce((s, f) => s + f.amount, 0);

  return (
    <Layout title="Student Profile">
      <div className="student-profile-layout">
        {/* Left: Photo & Quick Info */}
        <div className="student-profile-sidebar">
          <div className="card" style={{ textAlign: 'center' }}>
            {student.photo ? (
              <img src={student.photo.startsWith('/') ? student.photo : `/uploads/${student.photo}`} alt={student.name}
                style={{ width: 120, height: 120, borderRadius: 12, objectFit: 'cover', border: '3px solid #e2e8f0', marginBottom: 12 }} />
            ) : (
              <div style={{ width: 120, height: 120, borderRadius: 12, background: '#ebf8ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48, margin: '0 auto 12px' }}>👤</div>
            )}
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{student.name}</h2>
            <p style={{ fontSize: 12, color: '#718096', marginBottom: 8 }}>{student.institute}</p>
            <span className={`badge ${student.status === 'Active' ? 'badge-success' : 'badge-secondary'}`}>{student.status}</span>
            <div style={{ marginTop: 16, padding: '12px 0', borderTop: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#2d3748' }}>{student.room}</div>
              <div style={{ fontSize: 11, color: '#718096' }}>Assigned Room</div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn btn-warning btn-sm" style={{ flex: 1 }} onClick={() => navigate(`/admin/students/${id}/edit`)}>✏️ Edit</button>
              <button className="btn btn-outline btn-sm" style={{ flex: 1 }} onClick={() => navigate('/admin/students')}>← Back</button>
            </div>
          </div>
        </div>

        {/* Right: Details */}
        <div className="student-profile-main">
          <div className="card">
            <div className="card-header"><h3 className="card-title">👤 Personal Information</h3></div>
            <div className="student-profile-info-grid">
              <InfoRow label="Father Name" value={student.fatherName} />
              <InfoRow label="CNIC No" value={student.cnicNo} mono />
              <InfoRow label="Contact No" value={student.contactNo} />
              <InfoRow label="Emergency Contact" value={student.emergencyParentContact} />
              <InfoRow label="Department" value={student.department} />
              <InfoRow label="Admission Date" value={student.admissionDate?.split('T')[0]} />
              <InfoRow label="Address" value={student.address} full />
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h3 className="card-title">💰 Fee Structure</h3></div>
            <div className="student-fee-grid">
              <div style={{ background: '#f7fafc', borderRadius: 8, padding: 14, textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700 }}>PKR {(student.admissionFee || 0).toLocaleString()}</div>
                <div style={{ fontSize: 11, color: '#718096', marginTop: 4 }}>Admission Fee</div>
                <span className={`badge ${student.admissionFeePaid === 'Paid' ? 'badge-success' : 'badge-danger'}`} style={{ marginTop: 6, display: 'inline-block' }}>{student.admissionFeePaid}</span>
              </div>
              <div style={{ background: '#f7fafc', borderRadius: 8, padding: 14, textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700 }}>PKR {(student.securityFee || 0).toLocaleString()}</div>
                <div style={{ fontSize: 11, color: '#718096', marginTop: 4 }}>Security Fee</div>
                <span className={`badge ${student.securityFeePaid === 'Paid' ? 'badge-success' : 'badge-danger'}`} style={{ marginTop: 6, display: 'inline-block' }}>{student.securityFeePaid}</span>
              </div>
              <div style={{ background: '#f7fafc', borderRadius: 8, padding: 14, textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700 }}>PKR {(student.monthlyFee || 0).toLocaleString()}</div>
                <div style={{ fontSize: 11, color: '#718096', marginTop: 4 }}>Monthly Fee</div>
                <div style={{ marginTop: 6, fontSize: 11, color: '#718096' }}>Per month</div>
              </div>
            </div>
          </div>

          {/* Monthly Fee History */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">📋 Fee History</h3>
              <div className="flex gap-10">
                <span className="badge badge-success">Paid: PKR {totalPaid.toLocaleString()}</span>
                <span className="badge badge-danger">Pending: PKR {totalUnpaid.toLocaleString()}</span>
              </div>
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Month</th><th>Amount</th><th>Fine</th><th>Status</th><th>Paid Date</th></tr>
                </thead>
                <tbody>
                  {fees.length === 0 ? (
                    <tr><td colSpan={5} className="text-center" style={{ padding: 20, color: '#718096' }}>No fee records yet</td></tr>
                  ) : fees.map(f => (
                    <tr key={f._id}>
                      <td>{f.monthLabel}</td>
                      <td>PKR {f.amount.toLocaleString()}</td>
                      <td>{f.fine > 0 ? `PKR ${f.fine.toLocaleString()}` : '-'}</td>
                      <td><span className={`badge ${f.status === 'Paid' ? 'badge-success' : 'badge-danger'}`}>{f.status}</span></td>
                      <td style={{ fontSize: 12 }}>{f.paidDate ? f.paidDate.split('T')[0] : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function InfoRow({ label, value, mono, full }) {
  return (
    <div style={{ gridColumn: full ? '1/-1' : undefined }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#2d3748', fontFamily: mono ? 'monospace' : 'inherit' }}>{value || '-'}</div>
    </div>
  );
}
